import React from "react"
import PropTypes from "prop-types"
import TextField from "@mui/material/TextField"
import IconButton from "@mui/material/IconButton"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import Popover from "@mui/material/Popover"
import Chip from "@mui/material/Chip"
import Divider from "@mui/material/Divider"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"

import { useStateTagLogic } from "./useStateTagLogic"

const StateComponent = ({
  projectRow = null,
  isInCardProject = false,
  isInGrid = false,
}) => {
  const {
    // Estados
    anchorEl,
    open,
    showInputIdx,
    inputValue,
    setInputValue,
    setShowInputIdx,

    // Datos computados
    tagsByState,
    currentStateTag,
    options,

    // Funciones
    handleClick,
    handleClose,
    createStateTag,
    createAndAssignDefaultTag,
    deleteTag,
    handleKeyDown,
  } = useStateTagLogic(projectRow)

  return (
    <Box
      sx={
        isInCardProject
          ? {}
          : {
              height: "100%",
              display: "flex",
            }
      }
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          width: "140px",
          maxWidth: "100%",
          marginTop: isInGrid ? "5px" : "0px",
        }}
      >
        {currentStateTag ? (
          <Chip
            label={currentStateTag.name}
            size="small"
            sx={{
              fontSize: 12,
              borderRadius: "2px",
              backgroundColor: currentStateTag.color,
              flexGrow: 1,
              maxWidth: "fit-content",
            }}
          />
        ) : (
          <div></div>
        )}
        {!isInCardProject && (
          <IconButton
            size="small"
            onClick={handleClick}
            sx={{
              backgroundColor: "#eeeeee",
              borderRadius: "4px",
              padding: "3px",
              flexShrink: 0,
            }}
          >
            {currentStateTag ? (
              <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
            ) : (
              <AddIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        )}
      </Box>

      {!isInCardProject && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { p: 1, minWidth: 208 } } }}
        >
          {options.map((option, idx) => (
            <React.Fragment key={option.label}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "start",
                  gap: 1,
                  flexDirection: "column",
                  py: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: 12, ml: 1 }}>
                    {option.label}
                  </Typography>
                  <IconButton size="small" onClick={() => setShowInputIdx(idx)}>
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                {showInputIdx === idx && (
                  <Box sx={{ width: "100%", px: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      value={inputValue}
                      onChange={e => setInputValue(e.target.value)}
                      placeholder="Nueva etiqueta"
                      onKeyDown={e => handleKeyDown(e, option)}
                      inputProps={{ maxLength: 20 }}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: 12,
                          py: 0.5,
                          px: 1,
                          height: 24,
                        },
                        "& .MuiOutlinedInput-root": {
                          minHeight: 30,
                        },
                      }}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    ml: 1,
                    display: "flex",
                    flexDirection: "column",
                    flexWrap: "wrap",
                    gap: 0.5,
                    width: "-webkit-fill-available",
                  }}
                >
                  {option.tags &&
                    option.tags.map(
                      defaultTagObj =>
                        !tagsByState[option.state]?.some(
                          tag =>
                            tag.properties.name &&
                            tag.properties.name.toLowerCase() ===
                              (defaultTagObj.name || "").toLowerCase(),
                        ) && (
                          <Box
                            key={
                              option.state + "-default-" + defaultTagObj.name
                            }
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Chip
                              label={defaultTagObj.name}
                              size="small"
                              onClick={() =>
                                createAndAssignDefaultTag({
                                  ...option,
                                  tag: defaultTagObj.name,
                                })
                              }
                              sx={{
                                fontSize: 12,
                                borderRadius: "2px",
                                cursor: "pointer",
                                backgroundColor: `${defaultTagObj.color || option.color} !important`,
                              }}
                            />
                          </Box>
                        ),
                    )}
                  {(tagsByState[option.state] || []).map(tag => (
                    <Box
                      key={tag.SK}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        justifyContent: "space-between",
                        width: "100%",
                        position: "relative",
                        ":hover .delete-tag-btn": {
                          opacity: 1,
                          pointerEvents: "auto",
                        },
                      }}
                    >
                      <Chip
                        label={tag.properties.name}
                        size="small"
                        onClick={() => createStateTag(tag)}
                        sx={{
                          fontSize: 12,
                          borderRadius: "2px",
                          cursor: "pointer",
                          backgroundColor: `${tag.properties.color || option.color} !important`,
                        }}
                      />
                      {!tag.SK.startsWith("local-") &&
                        !options.some(
                          opt =>
                            opt.state === tag.properties.state &&
                            Array.isArray(opt.tags) &&
                            opt.tags.some(
                              t =>
                                t &&
                                t.name &&
                                tag.properties.name &&
                                t.name.toLowerCase() ===
                                  tag.properties.name.toLowerCase(),
                            ),
                        ) && (
                          <IconButton
                            className="delete-tag-btn"
                            size="small"
                            sx={{
                              p: 0.5,
                              opacity: 0,
                              pointerEvents: "none",
                              transition: "opacity 0.2s",
                              position: "absolute",
                              right: 0,
                            }}
                            onClick={() => deleteTag(tag)}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                    </Box>
                  ))}
                </Box>
              </Box>
              {idx < options.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Popover>
      )}
    </Box>
  )
}

StateComponent.propTypes = {
  projectRow: PropTypes.object,
  isInCardProject: PropTypes.bool,
  isInGrid: PropTypes.bool,
}

export default StateComponent
